/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import withStyles from '@mui/styles/withStyles';
import MenuMui from '@mui/material/Menu';
import MenuItemMui from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

import useIconParrot from 'hooks/useIconParrot';
import useUserStore from 'hooks/useUserStore';
import useMenuMakerDrawerState from 'hooks/useMenuMakerDrawerState';

import { getCloseDrawerFunction } from 'components/GeneralDrawer/MainView';
import { MENU_ROUTES } from 'utils/menuMaker/constants';

const Menu = withStyles((theme) => ({
  paper: {
    background: theme.palette.background.default,
    boxShadow: `0px 8px 40px  ${theme.palette.darkGrey[5]}`,
    borderRadius: '4px',
    padding: theme.spacing(1),
  },
}))((props) => (
  <MenuMui
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    elevation={0}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
));

const MenuItem = withStyles((theme) => ({
  root: {
    fontWeight: theme.typography.fontWeightMedium,
  },
}))(MenuItemMui);

function ActionsMenuMaker({
  isLoadingActionDelete = false,
  open,
  onEdit,
  onDuplicate,
  onCopy,
  onDelete,
  onClose,
  onPreview,
  onDeleteAll,
  ...others
}) {
  const actionRef = useRef();
  const { t } = useTranslation();
  const location = useLocation();
  const {
    storeState: { selected: storeSelected },
  } = useUserStore();
  const {
    IconDeleteRed,
    IconDeleteRed2,
    IconDeleteRed3,
    IconDuplicateOutline,
    IconCopyOutline,
    IconPreviewOutline,
    IconEdit,
  } = useIconParrot({
    icons: ['deleteRed', 'deleteRed2', 'deleteRed3', 'duplicateOutline', 'copyOutline', 'previewOutline', 'edit'],
  });
  const { keepOpen, setKeepOpen } = useMenuMakerDrawerState();
  const isMenuTab = location.pathname === MENU_ROUTES.MENUS;

  useEffect(() => {
    if (!keepOpen && actionRef.current) {
      actionRef.current();
    }

    setKeepOpen(undefined);
    actionRef.current = null;
  }, [keepOpen]);

  const handleAction = (action) => {
    setKeepOpen(undefined);
    const closeMenuMakerDrawer = getCloseDrawerFunction();

    if (closeMenuMakerDrawer) {
      actionRef.current = action;
      closeMenuMakerDrawer();
    } else {
      action();
    }
  };

  return (
    <Menu data-testid="actionsMenuMaker" keepMounted onClose={onClose} open={open} {...others}>
      {onEdit && (
        <MenuItem data-testid="editItemButton" onClick={onEdit}>
          <ListItemIcon>
            <IconEdit />
          </ListItemIcon>
          <ListItemText primary={t('common:actions.edit')} />
        </MenuItem>
      )}
      {onDuplicate && (
        <MenuItem data-testid="duplicateItemButton" onClick={() => handleAction(onDuplicate)}>
          <ListItemIcon>
            <IconDuplicateOutline />
          </ListItemIcon>
          <ListItemText primary={t('common:actions.duplicate')} />
        </MenuItem>
      )}
      {storeSelected?.isDraft && onCopy && (
        <MenuItem data-testid="copyItemButton" onClick={() => handleAction(onCopy)}>
          <ListItemIcon>
            <IconCopyOutline />
          </ListItemIcon>
          <ListItemText primary={t('common:actions.copyAnotherBrand')} />
        </MenuItem>
      )}
      {onPreview && (
        <MenuItem data-testid="previewItemButton" onClick={() => handleAction(onPreview)}>
          <ListItemIcon>
            <IconPreviewOutline />
          </ListItemIcon>
          <ListItemText primary={t('common:actions.preview')} />
        </MenuItem>
      )}
      {onDelete && (
        <MenuItem data-testid="deleteItemButton" onClick={() => handleAction(onDelete)}>
          <ListItemIcon>
            {isLoadingActionDelete ? (
              <CircularProgress color="inherit" size={24} style={{ color: '#DF130E' }} />
            ) : (
              <IconDeleteRed2 />
            )}
          </ListItemIcon>
          <ListItemText>
            <Box color="error.main" component="span">
              {isMenuTab ? t('common:actions.deleteOnlyMenu') : t('common:actions.delete')}
            </Box>
          </ListItemText>
        </MenuItem>
      )}
      {onDeleteAll && (
        <MenuItem
          data-testid="deleteItemButton"
          onClick={() => {
            handleAction(onDeleteAll);
          }}
        >
          <ListItemIcon>
            <IconDeleteRed3 />
          </ListItemIcon>
          <ListItemText>
            <Box color="error.main" component="span">
              {t('common:actions.deleteAll')}
            </Box>
          </ListItemText>
        </MenuItem>
      )}
    </Menu>
  );
}

ActionsMenuMaker.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  onDuplicate: PropTypes.func,
  onCopy: PropTypes.func,
  onDelete: PropTypes.func,
  onPublish: PropTypes.func,
  onPreview: PropTypes.func,
  t: PropTypes.func,
};

export default ActionsMenuMaker;
